'use client';

import React, { useMemo, useState } from 'react';

import { Box, Stack } from '@chakra-ui/react';

import { Loader } from '@components/custom-loader';
import { OrderDirection } from '@enums/index';
import {
    useGetBlackoutDatesQuery,
    useGetMentorAvailabilityQuery,
    useLazyGetMentorAvailabilityIntervalsQuery,
} from '@modules/availability/api';
import { SlotToBook, SlotWithId } from '@modules/find-mentor/types';
import { isSameDayAsToday } from '@utils/date';

import SessionAvailability from '../sections/SessionAvailability';
import SessionCalendar, { SelectedDateType } from '../sections/SessionCalendar';

type BookSessionCardProps = {
    onNext: () => void;
    slug: string;
    setSelectedSlot: React.Dispatch<React.SetStateAction<SlotToBook | undefined>>;
};

const BookSessionCard = ({ onNext, slug, setSelectedSlot }: BookSessionCardProps) => {
    const [slotsForDay, setSlotsForDay] = useState<SlotWithId[]>([]);
    const [selectedDate, setSelectedDate] = useState<SelectedDateType>(new Date());
    const { currentData, isFetching } = useGetMentorAvailabilityQuery({
        username: slug,
    });
    const { data, isFetching: blackoutLoading } = useGetBlackoutDatesQuery({
        offset: 0,
        limit: 100,
        orderBy: 'createdAt',
        orderDirection: OrderDirection.Desc,
        username: slug,
    });

    const [getIntervals, { currentData: availabilityForDay, isFetching: availabilityFetching }] =
        useLazyGetMentorAvailabilityIntervalsQuery();

    const isTodaySelected = useMemo(() => isSameDayAsToday(selectedDate as Date), [selectedDate]);
    return (
        <Box
            width={{ base: '100%', sm: '100%', md: '50vw', xl: '38vw' }}
            minHeight="auto"
            p={{ base: 3, sm: 3, md: "24px" }}
            border={1}
            borderStyle="solid"
            borderColor="border"
            borderRadius={6}
            position="relative"
        >
            {(isFetching || blackoutLoading) && (
                <Loader
                    bgColor="loaderBg"
                    height="100%"
                    width="100%"
                    size="lg"
                    position="absolute"
                />
            )}
            <Stack
                flexDir={{ base: 'column', sm: 'column' }}
                width="100%"
                gap={2}
                align="flex-start"
                justify="space-between"
            >
                <SessionCalendar
                    setSlotsForDay={setSlotsForDay}
                    setSelectedDate={setSelectedDate}
                    selectedDate={selectedDate}
                    availabilityList={currentData!}
                    blackoutDates={data?.blackouts!}
                    slug={slug}
                    availabilityForDay={availabilityForDay!}
                    getIntervals={getIntervals}
                />
                <SessionAvailability
                    setSelectedSlot={setSelectedSlot}
                    isTodaySelected={isTodaySelected}
                    slotsForDay={slotsForDay}
                    onNext={onNext}
                    isLoading={availabilityFetching}
                />
            </Stack>
        </Box>
    );
};

export default BookSessionCard;
